// import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
// import './mobileAdminComponents.css';

// type Props = {
//     open: boolean;
//     title: string;
//     description: string;
//     confirmLabel: string;
//     confirmationText?: string;
//     requireReason?: boolean;
//     reasonLabel?: string;
//     reasonMinLength?: number;
//     danger?: boolean;
//     busy?: boolean;
//     onCancel: () => void;
//     onConfirm: (value: { reason?: string; confirmation?: string }) => void;
// };

// export function ConfirmationDialog({ open, title, description, confirmLabel, confirmationText, requireReason = false, reasonLabel = 'Reason', reasonMinLength = 3, danger = false, busy = false, onCancel, onConfirm }: Props) {
//     const [reason, setReason] = useState('');
//     const [confirmation, setConfirmation] = useState('');
//     const cancelRef = useRef<HTMLButtonElement>(null);
//     const returnFocusRef = useRef<HTMLElement | null>(null);
//     const titleId = useId();
//     const descriptionId = useId();

//     useEffect(() => {
//         if (!open) return;
//         returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
//         setReason('');
//         setConfirmation('');
//         cancelRef.current?.focus();
//         const onKeyDown = (event: KeyboardEvent) => {
//             if (event.key === 'Escape' && !busy) onCancel();
//         };
//         window.addEventListener('keydown', onKeyDown);
//         return () => {
//             window.removeEventListener('keydown', onKeyDown);
//             returnFocusRef.current?.focus();
//             returnFocusRef.current = null;
//         };
//     }, [open, busy, onCancel]);

//     if (!open) return null;
//     const validReason = !requireReason || reason.trim().length >= reasonMinLength;
//     const validConfirmation = !confirmationText || confirmation === confirmationText;
//     const submit = (event: FormEvent) => {
//         event.preventDefault();
//         if (validReason && validConfirmation && !busy) onConfirm({ reason: reason.trim() || undefined, confirmation: confirmation || undefined });
//     };

//     return (
//         <div className="ma-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
//             <section className="ma-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId}>
//                 <h2 id={titleId}>{title}</h2>
//                 <p id={descriptionId}>{description}</p>
//                 <form onSubmit={submit}>
//                     {requireReason && <div className="ma-dialog__field"><label htmlFor={`${descriptionId}-reason`}>{reasonLabel}</label><textarea id={`${descriptionId}-reason`} aria-describedby={`${descriptionId}-reason-help`} placeholder={`Enter at least ${reasonMinLength} characters`} value={reason} onChange={event => setReason(event.target.value)} rows={3} required minLength={reasonMinLength} autoComplete="off" /><small id={`${descriptionId}-reason-help`}>{reason.length === 0 ? `Required: at least ${reasonMinLength} characters.` : validReason ? 'Reason accepted.' : `${Math.max(0, reasonMinLength - reason.trim().length)} more character(s) required.`}</small></div>}
//                     {confirmationText && <div className="ma-dialog__field"><label htmlFor={`${descriptionId}-confirmation`}>Type this exact phrase to confirm:</label><strong className="ma-dialog__confirmation-phrase">{confirmationText}</strong><input id={`${descriptionId}-confirmation`} aria-describedby={`${descriptionId}-confirmation-help`} placeholder={confirmationText} value={confirmation} onChange={event => setConfirmation(event.target.value)} autoComplete="off" spellCheck={false} /><small id={`${descriptionId}-confirmation-help`}>{confirmation.length === 0 ? 'Required: the phrase is case-sensitive.' : validConfirmation ? 'Confirmation matched.' : 'The confirmation does not match the required phrase.'}</small></div>}
//                     <div className="ma-dialog__actions">
//                         <button ref={cancelRef} type="button" className="ma-button ma-button--secondary" onClick={onCancel} disabled={busy}>Cancel</button>
//                         <button type="submit" className={`ma-button ${danger ? 'ma-button--danger' : 'ma-button--primary'}`} disabled={!validReason || !validConfirmation || busy}>{busy ? 'Working…' : confirmLabel}</button>
//                     </div>
//                 </form>
//             </section>
//         </div>
//     );
// }

import React, { FormEvent, useEffect, useId, useRef, useState } from "react";

import "./mobileAdminComponents.css";

type Props = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmationText?: string;
  requireReason?: boolean;
  reasonLabel?: string;
  reasonMinLength?: number;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (value: { reason?: string; confirmation?: string }) => void;
};

/* =========================================================
   ICONS
========================================================= */

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 6l12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M10.3 4.7 3.5 17.1A2 2 0 0 0 5.3 20h13.4a2 2 0 0 0 1.8-2.9L13.7 4.7a2 2 0 0 0-3.4 0Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />

    <path
      d="M12 9v4.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />

    <circle cx="12" cy="16.5" r="1" fill="currentColor" />
  </svg>
);

const ConfirmIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 3 19 6v5c0 4.6-2.8 8-7 10-4.2-2-7-5.4-7-10V6l7-3Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />

    <path
      d="m9 12 2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="m6 12 4 4 8-9"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CopyIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="8"
      y="8"
      width="11"
      height="11"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.7"
    />

    <path
      d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
);

const Spinner = () => (
  <span
    className="
            inline-block
            h-4 w-4
            animate-spin
            rounded-full
            border-2
            border-white/30
            border-t-white
        "
    aria-hidden="true"
  />
);

/* =========================================================
   COMPONENT
========================================================= */

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmationText,
  requireReason = false,
  reasonLabel = "Reason",
  reasonMinLength = 3,
  danger = false,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");

  const [confirmation, setConfirmation] = useState("");

  const [copied, setCopied] = useState(false);

  const cancelRef = useRef<HTMLButtonElement>(null);

  const reasonRef = useRef<HTMLTextAreaElement>(null);

  const returnFocusRef = useRef<HTMLElement | null>(null);

  const titleId = useId();
  const descriptionId = useId();

  const reasonId = `${descriptionId}-reason`;

  const reasonHelpId = `${descriptionId}-reason-help`;

  const confirmationId = `${descriptionId}-confirmation`;

  const confirmationHelpId = `${descriptionId}-confirmation-help`;

  /* =====================================================
       OPEN / CLOSE EFFECTS
    ===================================================== */

  useEffect(() => {
    if (!open) {
      return;
    }

    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    setReason("");
    setConfirmation("");
    setCopied(false);

    /*
     * Prevent page behind modal from scrolling.
     */
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    /*
     * Give the dialog initial focus.
     *
     * If a reason is required, starting there is
     * more convenient than focusing Cancel.
     */
    window.setTimeout(() => {
      if (requireReason && reasonRef.current) {
        reasonRef.current.focus();
      } else {
        cancelRef.current?.focus();
      }
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) {
        onCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);

      document.body.style.overflow = previousOverflow;

      returnFocusRef.current?.focus();

      returnFocusRef.current = null;
    };
  }, [open, busy, onCancel, requireReason]);

  /* =====================================================
       VALIDATION
    ===================================================== */

  if (!open) {
    return null;
  }

  const trimmedReason = reason.trim();

  const validReason = !requireReason || trimmedReason.length >= reasonMinLength;

  const validConfirmation =
    !confirmationText || confirmation === confirmationText;

  const valid = validReason && validConfirmation;

  const remainingReasonCharacters = Math.max(
    0,
    reasonMinLength - trimmedReason.length,
  );

  const confirmationStarted = confirmation.length > 0;

  /* =====================================================
       SUBMIT
    ===================================================== */

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!validReason || !validConfirmation || busy) {
      return;
    }

    onConfirm({
      reason: trimmedReason || undefined,

      confirmation: confirmation || undefined,
    });
  };

  /* =====================================================
       COPY CONFIRMATION
    ===================================================== */

  const copyConfirmation = async () => {
    if (!confirmationText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(confirmationText);

      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1400);
    } catch {
      /*
       * Clipboard access can be unavailable
       * depending on browser security settings.
       */
    }
  };

  /* =====================================================
       RENDER
    ===================================================== */

  return (
    <div
      role="presentation"
      className="
                fixed inset-0
                z-[1200]
                flex
                items-center
                justify-center
                bg-slate-950/45
                p-4
                backdrop-blur-[1px]
            "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="
                    flex
                    max-h-[calc(100vh-32px)]
                    w-full
                    max-w-[560px]
                    flex-col
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-slate-200
                    bg-white
                    shadow-[0_24px_80px_rgba(15,23,42,0.26)]
                "
      >
        {/* =========================================
                    HEADER
                ========================================= */}

        <header
          className="
                        flex
                        shrink-0
                        items-start
                        justify-between
                        gap-4
                        border-b
                        border-slate-100
                        px-6 py-5
                        max-sm:px-4
                    "
        >
          <div
            className="
                            flex
                            min-w-0
                            items-start
                            gap-3
                        "
          >
            <div
              className={`
                                grid
                                h-10 w-10
                                shrink-0
                                place-items-center
                                rounded-[11px]

                                ${
                                  danger
                                    ? "bg-red-50 text-red-600"
                                    : "bg-slate-100 text-slate-700"
                                }
                            `}
            >
              {danger ? <WarningIcon /> : <ConfirmIcon />}
            </div>

            <div className="min-w-0">
              <h2
                id={titleId}
                className="
                                    m-0
                                    text-[19px]
                                    font-bold
                                    leading-6
                                    tracking-[-0.02em]
                                    text-slate-950
                                "
              >
                {title}
              </h2>

              <div
                className={`
                                    mt-1.5
                                    inline-flex
                                    items-center
                                    rounded-full
                                    px-2 py-0.5
                                    text-[9px]
                                    font-bold
                                    uppercase
                                    tracking-[0.06em]

                                    ${
                                      danger
                                        ? "bg-red-50 text-red-600"
                                        : "bg-slate-100 text-slate-500"
                                    }
                                `}
              >
                {danger ? "Confirmation required" : "Confirm action"}
              </div>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close dialog"
            disabled={busy}
            onClick={onCancel}
            className="
                            grid
                            h-8 w-8
                            shrink-0
                            place-items-center
                            rounded-lg
                            border-0
                            bg-transparent
                            p-0
                            text-slate-400
                            transition
                            hover:bg-slate-100
                            hover:text-slate-900
                            focus:outline-none
                            focus:ring-2
                            focus:ring-slate-200
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                        "
          >
            <CloseIcon />
          </button>
        </header>

        {/* =========================================
                    FORM
                ========================================= */}

        <form
          onSubmit={submit}
          className="
                        flex
                        min-h-0
                        flex-1
                        flex-col
                        overflow-hidden
                    "
        >
          {/* =====================================
                        SCROLLABLE CONTENT
                    ===================================== */}

          <div
            className="
                            min-h-0
                            flex-1
                            overflow-y-auto
                            px-6 py-5
                            max-sm:px-4
                        "
          >
            {/* Description */}

            <div
              id={descriptionId}
              className={`
                                rounded-[11px]
                                border
                                px-4 py-3

                                ${
                                  danger
                                    ? "border-red-100 bg-red-50/60"
                                    : "border-slate-200 bg-slate-50"
                                }
                            `}
            >
              <p
                className={`
                                    m-0
                                    text-[12px]
                                    leading-5

                                    ${
                                      danger ? "text-red-800" : "text-slate-600"
                                    }
                                `}
              >
                {description}
              </p>
            </div>

            {/* =================================
                            REASON
                        ================================= */}

            {requireReason && (
              <div className="mt-5">
                <div
                  className="
                                        mb-1.5
                                        flex
                                        items-center
                                        justify-between
                                        gap-3
                                    "
                >
                  <label
                    htmlFor={reasonId}
                    className="
                                            text-[12px]
                                            font-semibold
                                            text-slate-700
                                        "
                  >
                    {reasonLabel}

                    <span className="ml-1 text-red-500">*</span>
                  </label>

                  <span
                    className="
                                            text-[10px]
                                            text-slate-400
                                        "
                  >
                    Min. {reasonMinLength} characters
                  </span>
                </div>

                <textarea
                  ref={reasonRef}
                  id={reasonId}
                  aria-describedby={reasonHelpId}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  required
                  minLength={reasonMinLength}
                  disabled={busy}
                  autoComplete="off"
                  placeholder="Explain why this action is being performed..."
                  className={`
                                        box-border
                                        min-h-[86px]
                                        w-full
                                        resize-y
                                        rounded-[10px]
                                        border
                                        bg-white
                                        px-3
                                        py-2.5
                                        text-[13px]
                                        leading-5
                                        text-slate-900
                                        outline-none
                                        transition
                                        placeholder:text-slate-400
                                        focus:ring-2
                                        disabled:cursor-not-allowed
                                        disabled:bg-slate-50

                                        ${
                                          reason.length > 0 && !validReason
                                            ? "border-amber-300 focus:border-amber-400 focus:ring-amber-100"
                                            : validReason && reason.length > 0
                                              ? "border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100"
                                              : "border-slate-200 hover:border-slate-300 focus:border-slate-400 focus:ring-slate-100"
                                        }
                                    `}
                />

                <div
                  id={reasonHelpId}
                  className="
                                        mt-1.5
                                        min-h-[16px]
                                        text-[10px]
                                    "
                >
                  {reason.length === 0 ? (
                    <span className="text-slate-400">
                      Required for the audit history.
                    </span>
                  ) : validReason ? (
                    <span
                      className="
                                                inline-flex
                                                items-center
                                                gap-1
                                                font-medium
                                                text-emerald-600
                                            "
                    >
                      <CheckIcon />
                      Reason accepted
                    </span>
                  ) : (
                    <span className="text-amber-600">
                      {remainingReasonCharacters} more{" "}
                      {remainingReasonCharacters === 1
                        ? "character"
                        : "characters"}{" "}
                      required.
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* =================================
                            CONFIRMATION PHRASE
                        ================================= */}

            {confirmationText && (
              <div className="mt-5">
                <label
                  htmlFor={confirmationId}
                  className="
                                        block
                                        text-[12px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Confirm this action
                </label>

                <p
                  className="
                                        mb-2 mt-1
                                        text-[10px]
                                        leading-4
                                        text-slate-400
                                    "
                >
                  Type the exact case-sensitive phrase below to continue.
                </p>

                {/* Phrase */}

                <div
                  className="
                                        flex
                                        items-center
                                        justify-between
                                        gap-3
                                        rounded-[9px]
                                        border
                                        border-slate-200
                                        bg-slate-50
                                        px-3 py-2.5
                                    "
                >
                  <code
                    className="
                                            min-w-0
                                            break-all
                                            text-[12px]
                                            font-semibold
                                            tracking-[0.035em]
                                            text-slate-800
                                            select-all
                                        "
                  >
                    {confirmationText}
                  </code>

                  <button
                    type="button"
                    onClick={copyConfirmation}
                    disabled={busy}
                    className="
                                            inline-flex
                                            h-7
                                            shrink-0
                                            items-center
                                            gap-1.5
                                            rounded-md
                                            border
                                            border-slate-200
                                            bg-white
                                            px-2
                                            text-[10px]
                                            font-semibold
                                            text-slate-500
                                            transition
                                            hover:bg-slate-100
                                            hover:text-slate-800
                                            disabled:cursor-not-allowed
                                            disabled:opacity-50
                                        "
                  >
                    {copied ? (
                      <>
                        <CheckIcon />
                        Copied
                      </>
                    ) : (
                      <>
                        <CopyIcon />
                        Copy
                      </>
                    )}
                  </button>
                </div>

                {/* Input */}

                <input
                  id={confirmationId}
                  aria-describedby={confirmationHelpId}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  disabled={busy}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Type the confirmation phrase"
                  className={`
                                        mt-2.5
                                        box-border
                                        h-10
                                        w-full
                                        rounded-[9px]
                                        border
                                        bg-white
                                        px-3
                                        font-mono
                                        text-[12px]
                                        text-slate-900
                                        outline-none
                                        transition
                                        placeholder:font-sans
                                        placeholder:text-slate-400
                                        focus:ring-2
                                        disabled:cursor-not-allowed
                                        disabled:bg-slate-50

                                        ${
                                          confirmationStarted &&
                                          !validConfirmation
                                            ? "border-amber-300 focus:border-amber-400 focus:ring-amber-100"
                                            : confirmationStarted &&
                                                validConfirmation
                                              ? "border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100"
                                              : "border-slate-200 hover:border-slate-300 focus:border-slate-400 focus:ring-slate-100"
                                        }
                                    `}
                />

                <div
                  id={confirmationHelpId}
                  className="
                                        mt-1.5
                                        min-h-[16px]
                                        text-[10px]
                                    "
                >
                  {!confirmationStarted ? (
                    <span className="text-slate-400">
                      Confirmation phrase is required.
                    </span>
                  ) : validConfirmation ? (
                    <span
                      className="
                                                inline-flex
                                                items-center
                                                gap-1
                                                font-medium
                                                text-emerald-600
                                            "
                    >
                      <CheckIcon />
                      Confirmation matched
                    </span>
                  ) : (
                    <span className="text-amber-600">
                      The phrase does not match yet.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* =====================================
                        FOOTER
                    ===================================== */}

          <footer
            className="
                            flex
                            shrink-0
                            items-center
                            justify-between
                            gap-4
                            border-t
                            border-slate-100
                            bg-white
                            px-6 py-4
                            max-sm:flex-col-reverse
                            max-sm:px-4
                        "
          >
            <p
              className="
                                m-0
                                text-[10px]
                                text-slate-400
                                max-sm:hidden
                            "
            >
              This action may be recorded in the audit history.
            </p>

            <div
              className="
                                flex
                                items-center
                                gap-2
                                max-sm:w-full
                            "
            >
              <button
                ref={cancelRef}
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="
                                    h-10
                                    min-w-[96px]
                                    rounded-[9px]
                                    border
                                    border-slate-200
                                    bg-white
                                    px-4
                                    text-[12px]
                                    font-semibold
                                    text-slate-700
                                    transition
                                    hover:bg-slate-50
                                    hover:border-slate-300
                                    focus:outline-none
                                    focus:ring-2
                                    focus:ring-slate-200
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                    max-sm:flex-1
                                "
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!valid || busy}
                className={`
                                    inline-flex
                                    h-10
                                    min-w-[120px]
                                    items-center
                                    justify-center
                                    gap-2
                                    rounded-[9px]
                                    border-0
                                    px-4
                                    text-[12px]
                                    font-semibold
                                    text-white
                                    transition
                                    focus:outline-none
                                    focus:ring-2
                                    focus:ring-offset-2
                                    disabled:cursor-not-allowed
                                    disabled:opacity-40
                                    max-sm:flex-1

                                    ${
                                      danger
                                        ? "bg-red-600 hover:bg-red-700 focus:ring-red-200"
                                        : "bg-[#0d162a] hover:bg-[#172239] focus:ring-slate-300"
                                    }
                                `}
              >
                {busy && <Spinner />}

                {busy ? "Working..." : confirmLabel}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}
